import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { SubItemTreeViewState } from '@atom/components/common/subItemTree/subItemTreeReducer';
import WorkOrdersContext from '@atom/components/workOrders/WorkOrdersContext';
import { GET_WORK_ORDER } from '@atom/graph/work';
import { usePreviousValue } from '@atom/hooks/usePreviousValue';
import {
  INITIAL_WORK_VALIDATIONS,
  useWorkValidations,
} from '@atom/hooks/useWorkValidations';
import { Progress } from '@atom/mui';
import { TaskTemplate } from '@atom/types/taskTemplate';
import { WorkOrderDetailType, WorkOrderType } from '@atom/types/work';
import api from '@atom/utilities/api';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { TASK_TEMPLATES_ENDPOINT } from '@atom/utilities/endpoints';

import InfoView from './infoView/InfoView';
import MainView from './MainView';
import WorkOrderPreviewContext, {
  ExpandedInfo,
  reducer,
  View,
  WorkOrderActionTypes,
} from './WorkOrderPreviewContext';

import classes from './workOrderPreview.module.css';

const WorkOrderPreview = () => {
  const { input } = useContext(WorkOrdersContext);

  const { workValidations, setWorkValidations } = useWorkValidations();

  const [workOrderDetail, dispatch] = useReducer(
    reducer,
    {} as WorkOrderDetailType,
  );
  const [activeView, setActiveView] = useState<View>(View.MAIN);
  const [expandedInfo, setExpandedInfo] = useState<Set<ExpandedInfo>>(
    new Set([
      ExpandedInfo.LOCATION,
      ExpandedInfo.SUMMARY,
      ExpandedInfo.CUSTOM,
      ExpandedInfo.DETAIL,
    ]),
  );
  const [collapsedTasks, setCollapsedTasks] = useState<Set<string>>(
    new Set([]),
  );
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [loadingTaskTemplates, setLoadingTaskTemplates] =
    useState<boolean>(true);
  const [activeWorkOrderAsset, setActiveWorkOrderAsset] = useState<string>('');
  const [assetTreeViewState, setAssetTreeViewStateById] = useState<
    Record<string, SubItemTreeViewState>
  >({});
  const [activeWorkAssetTaskId, setActiveWorkAssetTaskId] =
    useState<string>('');
  const previousActiveWorkOrderAsset = usePreviousValue(activeWorkOrderAsset);

  const {
    data: workOrderData,
    loading,
    error,
    refetch,
  } = useQuery(GET_WORK_ORDER, {
    variables: {
      id: input.workOrderId,
      showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (workOrderData?.workOrder) {
      const mappedWorkOrder = {
        ...workOrderData?.workOrder,
        type: R.pathOr(
          WorkOrderType.DEFAULT,
          ['workOrder', 'type'],
          workOrderData,
        ),
      };

      dispatch({
        type: WorkOrderActionTypes.SET_WORK_ORDER,
        data: mappedWorkOrder,
      });
    }
  }, [workOrderData]);

  const resetState = () => {
    setActiveView(View.MAIN);
    setExpandedInfo(
      new Set([
        ExpandedInfo.LOCATION,
        ExpandedInfo.SUMMARY,
        ExpandedInfo.CUSTOM,
        ExpandedInfo.DETAIL,
      ]),
    );
    setCollapsedTasks(new Set([]));
    setTaskTemplates([]);
    setLoadingTaskTemplates(true);
    setActiveWorkOrderAsset('');
    setAssetTreeViewStateById({});
    setActiveWorkAssetTaskId('');
  };

  useEffect(() => {
    resetState();
    dispatch({
      type: WorkOrderActionTypes.SET_WORK_ORDER,
      // @ts-ignore
      data: {},
    });

    // If the current work order does not match the work order with validation
    // errors in global state, then clear it.
    if (input.workOrderId !== workValidations.workOrderId) {
      setWorkValidations(INITIAL_WORK_VALIDATIONS);
    }
  }, [input.workOrderId]);

  const setAssetTreeViewState = useCallback(
    (rootAssetId: string, viewState: SubItemTreeViewState) =>
      setAssetTreeViewStateById(previous => ({
        ...previous,
        [rootAssetId]: viewState,
      })),
    [],
  );

  const getTaskTemplates = async (): Promise<TaskTemplate[]> => {
    const { data } = await api.get(TASK_TEMPLATES_ENDPOINT, {
      workOrderId: workOrderDetail.id,
    });

    setTaskTemplates(data);
    setLoadingTaskTemplates(false);
    return data;
  };

  useEffect(() => {
    if (workOrderDetail.id) {
      getTaskTemplates();
    }
  }, [workOrderDetail.id]);

  useEffect(() => {
    if (R.isEmpty(activeWorkOrderAsset) && previousActiveWorkOrderAsset) {
      refetch();
    }
  }, [activeWorkOrderAsset, workOrderDetail.id]);

  const workOrderPreviewLoading = error || !workOrderDetail.id;

  const getContent = () => {
    const views = {
      [View.MAIN]: <MainView />,
      [View.INFO]: <InfoView />,
    };

    return views[activeView];
  };

  return (
    <WorkOrderPreviewContext.Provider
      value={{
        refetch,
        workOrderDetail,
        dispatch,
        loading,
        collapsedTasks,
        setCollapsedTasks,
        activeWorkOrderAsset,
        setActiveWorkOrderAsset,
        assetTreeViewState,
        setAssetTreeViewState,
        activeWorkAssetTaskId,
        setActiveWorkAssetTaskId,
        activeView,
        setActiveView,
        expandedInfo,
        setExpandedInfo,
        taskTemplates,
        setTaskTemplates,
        loadingTaskTemplates,
        refetchTaskTemplates: getTaskTemplates,
      }}
    >
      <div className={classes.trayContainer}>
        {workOrderPreviewLoading ? (
          <div className={classes.loadingContainer}>
            <Progress />
          </div>
        ) : (
          getContent()
        )}
      </div>
    </WorkOrderPreviewContext.Provider>
  );
};

export default WorkOrderPreview;
